import React, { useState, useEffect } from 'react';
import { Shield, Download, Users, AlertCircle, FileSpreadsheet, Eye, Home, Lock } from 'lucide-react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';

const SuperAdmin = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // 检查是否已经登录（从sessionStorage）
  useEffect(() => {
    const superAdminAuth = sessionStorage.getItem('superAdminAuth');
    if (superAdminAuth === 'true') {
      setIsAuthenticated(true);
      loadAllHistory();
    }
  }, []);

  // 验证密码
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setLoading(true);

    try {
      // 调用后端验证密码
      const response = await axios.post('/api/superadmin/verify', {
        password: password
      });

      if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('superAdminAuth', 'true');
        loadAllHistory();
      }
    } catch (err) {
      console.error('密码验证失败:', err);
      if (err.response?.status === 401) {
        setPasswordError('密码错误，请重试');
      } else {
        setPasswordError('验证失败: ' + (err.response?.data?.error || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  // 加载所有历史记录
  const loadAllHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/superadmin/history/all');
      setHistoryRecords(response.data.records || []);
      console.log('✅ 成功加载所有历史记录:', response.data.records?.length || 0);
    } catch (err) {
      console.error('加载历史记录失败:', err);
      alert('加载历史记录失败: ' + (err.response?.data?.error || err.message));
      setHistoryRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // 导出为Excel
  const exportToExcel = () => {
    try {
      // 准备Excel数据
      const excelData = historyRecords.map((record, index) => ({
        '序号': index + 1,
        '用户ID': record.user_id,
        '用户名': record.username || record.name || '未知用户',
        '组织ID': record.organization_id || '无',
        '网页内容描述': record.prompt || record.description || '无描述',
        '专业版式选择': record.layout || '未指定',
        '专业配色方案': record.colors || '未指定',
        'HTML源码': record.generated_html || '无',
        '操作时间': record.created_at ? new Date(record.created_at).toLocaleString('zh-CN') : '未知',
        '生成耗时(秒)': record.generation_time ? (record.generation_time / 1000).toFixed(2) : '未知',
        '文件大小(KB)': record.file_size ? (record.file_size / 1024).toFixed(2) : '未知',
      }));

      // 创建工作簿
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // 设置列宽
      ws['!cols'] = [
        { wch: 6 },   // 序号
        { wch: 20 },  // 用户ID
        { wch: 15 },  // 用户名
        { wch: 20 },  // 组织ID
        { wch: 40 },  // 网页内容描述
        { wch: 20 },  // 专业版式选择
        { wch: 20 },  // 专业配色方案
        { wch: 60 },  // HTML源码
        { wch: 20 },  // 操作时间
        { wch: 15 },  // 生成耗时
        { wch: 15 },  // 文件大小
      ];

      // 添加工作表到工作簿
      XLSX.utils.book_append_sheet(wb, ws, '所有历史记录');

      // 生成文件名
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `所有历史记录_${timestamp}.xlsx`;

      // 导出文件
      XLSX.writeFile(wb, filename);
      
      console.log('✅ 成功导出Excel文件:', filename);
    } catch (err) {
      console.error('导出Excel失败:', err);
      alert('导出失败: ' + err.message);
    }
  };

  // 预览HTML
  const handlePreviewHtml = (html) => {
    setPreviewHtml(html);
    setShowPreview(true);
  };

  // 下载单个HTML
  const handleDownloadHtml = (html, recordId) => {
    try {
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `generated_${recordId}.html`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('下载HTML失败:', err);
      alert('下载失败: ' + err.message);
    }
  };

  // 退出登录
  const handleLogout = () => {
    sessionStorage.removeItem('superAdminAuth');
    setIsAuthenticated(false);
    setPassword('');
    setHistoryRecords([]);
  };

  // 返回主页：在容器反代场景下增加硬跳转兜底
  const goHome = () => {
    try {
      navigate('/');
      setTimeout(() => {
        if (window.location.pathname === '/superadmin') {
          window.location.assign('/');
        }
      }, 120);
    } catch (_e) {
      window.location.assign('/');
    }
  };

  // 如果未认证，显示密码输入页面
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
              <Shield className="w-8 h-8 text-indigo-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">超级管理员</h1>
            <p className="text-gray-600">请输入管理员密码以访问</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                管理员密码
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="请输入密码"
                  required
                  autoFocus
                />
              </div>
              {passwordError && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {passwordError}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !password}
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>验证中...</span>
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  <span>验证并进入</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={goHome}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center space-x-1"
            >
              <Home className="w-4 h-4" />
              <span>返回主页</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 已认证，显示管理页面
  return (
    <div className="min-h-screen bg-gray-50">
      {/* 头部 */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white">超级管理员</h1>
                <p className="text-sm text-indigo-100">
                  所有用户历史记录 - 共 {historyRecords.length} 条记录
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={goHome}
                className="flex items-center space-x-2 px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
              >
                <Home className="w-4 h-4" />
                <span>返回主页</span>
              </button>
              <button
                onClick={exportToExcel}
                disabled={historyRecords.length === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span>导出Excel</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Lock className="w-4 h-4" />
                <span>退出</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 主内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
              <p className="text-gray-600">加载中...</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {historyRecords.length === 0 ? (
              <div className="p-16 text-center">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 text-lg">暂无历史记录</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        序号
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        用户名
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        组织ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        网页内容描述
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        专业版式选择
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        专业配色方案
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        HTML源码
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        操作时间
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {historyRecords.map((record, index) => (
                      <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {record.username || record.name || '未知用户'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.organization_id || '无'}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700 max-w-md">
                          <div className="line-clamp-3" title={record.prompt || record.description}>
                            {record.prompt || record.description || '无描述'}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                          {record.layout || '未指定'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                          {record.colors || '未指定'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm">
                          {record.generated_html ? (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handlePreviewHtml(record.generated_html)}
                                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                title="预览HTML"
                              >
                                <Eye className="w-4 h-4" />
                                <span>预览</span>
                              </button>
                              <button
                                onClick={() => handleDownloadHtml(record.generated_html, record.id)}
                                className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                title="下载HTML"
                              >
                                <Download className="w-4 h-4" />
                                <span>下载</span>
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-400">无</span>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.created_at ? new Date(record.created_at).toLocaleString('zh-CN') : '未知'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* HTML预览模态框 */}
      {showPreview && previewHtml && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-5/6 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">HTML预览</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                title="HTML预览"
                srcDoc={previewHtml}
                className="w-full h-full border-0"
                sandbox="allow-same-origin allow-scripts"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdmin;
